"use client";

import { Link } from "@/navigation";
import { useTranslations } from "next-intl";
import Image from "next/image";

/** 6 个行业卡片配置：标题与描述的翻译 key */
const INDUSTRY_KEYS = [
	{ titleKey: "industry1Title", descKey: "industry1Desc", href: "/industry/automotive" },
	{ titleKey: "industry2Title", descKey: "industry2Desc", href: "/industry/aerospace" },
	{ titleKey: "industry3Title", descKey: "industry3Desc", href: "/industry/medical-device" },
	{ titleKey: "industry4Title", descKey: "industry4Desc", href: "/industry/consumer-electronics" },
	{ titleKey: "industry5Title", descKey: "industry5Desc", href: "/industry/industry-equipment" },
	{ titleKey: "industry6Title", descKey: "industry6Desc", href: "/industry/consumer-goods" },
] as const;

export interface InjectionMoldingProductsByIndustryProps {
	translationNamespace?: string;
	/** 6 张行业图，与 INDUSTRY_KEYS 顺序一致：Automotive, Aerospace, Medical, Consumer Electronics, Industrial, Packaging */
	images: string[];
	/** 底部 CTA 按钮跳转链接 */
	ctaHref?: string;
}

export default function InjectionMoldingProductsByIndustry({
	translationNamespace = "injectionMoldingProductsByIndustry",
	images,
	ctaHref = "/upload",
}: InjectionMoldingProductsByIndustryProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full overflow-hidden bg-white">
			{/* 标题 + 引言：与 InjectionMoldingCapabilities 一致的宽度与内边距 */}
			<div className="px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:px-16 lg:py-16">
				<div className="mx-auto text-center">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t("intro")}</p>
				</div>
			</div>

			{/* 3x2 行业卡片网格：max-w-6xl 与 Capabilities 主内容区一致，网格与卡片内边距参考设计图 */}
			<div className="px-4 py-8 sm:px-6 sm:py-10 md:py-12 lg:px-16 lg:py-16">
				<div className="container max-w-6xl">
					<div className="grid w-full gap-6 sm:gap-8 md:grid-cols-2 lg:gap-10">
						{INDUSTRY_KEYS.map((item, index) => (
							<Link
								key={item.titleKey}
								href={item.href}
								className="flex overflow-hidden rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md"
							>
								{/* 左侧图片：比例 290:185 */}
								<div className="relative h-auto w-[42%] min-w-[120px] shrink-0 sm:min-w-[140px]">
									<div className="relative aspect-[290/185] w-full">
										<Image
											src={images[index] ?? ""}
											alt={t(item.titleKey)}
											fill
											className="rounded-l-xl object-cover"
											sizes="(max-width: 640px) 120px, 180px"
										/>
									</div>
								</div>
								{/* 右侧标题 + 描述：内边距与参考图一致，留足空白 */}
								<div className="flex flex-1 flex-col justify-center p-5 sm:p-6 md:p-6 lg:p-8">
									<h3 className="mb-2 text-lg font-bold text-themePrimary400 sm:text-xl md:text-2xl">
										{t(item.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-gray-900 md:text-base">{t(item.descKey)}</p>
								</div>
							</Link>
						))}
					</div>
				</div>
			</div>

			{/* 底部 CTA 按钮：与 Capabilities 一致的 padding 与 max-w */}
			<div className="border-t border-gray-100 px-4">
				<div className="mx-auto max-w-3xl text-center">
					<a
						href={ctaHref}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center justify-center rounded-lg bg-themePrimary400 px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-opacity-90 sm:px-10 sm:py-4 sm:text-lg"
					>
						{t("ctaButton")}
					</a>
				</div>
			</div>
		</section>
	);
}
